import { TableBody, TableRow, TableCell, Link, Chip, Checkbox, Tooltip, IconButton, useTheme, Stack, Box, Typography, CircularProgress, Menu, MenuItem, Button } from '@mui/material';
import { EnhancedTableColumn, EnhancedTableColumnType } from '.';
import { formatCurrencyNumber, formatDate, formatDateTime, formatIntPhoneNumber } from '../../helpers/formatting-utils';
import LinearProgress from '@mui/material/LinearProgress';
import dayjs from 'dayjs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useState, useRef, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import chroma from 'chroma-js';
import JsBarcode from 'jsbarcode';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface EnhancedTableBodyProps {
  header: EnhancedTableColumn[];
  data: Record<string, Record<string, any>>;
  selected: string[];
  handleCheckboxClick: (key: string) => void;
  boxedCells?: boolean;
  boxedCellClass?: string;
  striped?: boolean;
  stripedColors?: string[] | ((theme: any) => string[]);
  actionsColumnId?: string;
  rowActions?: any[];
  rowActionsOutsideMenu?: boolean;
  addingRow?: boolean;
  editingRowId?: string | null;
  editRowValues?: Record<string, any>;
  editRowErrors?: Record<string, string>;
  onEditInputChange?: (colId: string, value: any) => void;
  onEditCancel?: () => void;
  onEditSave?: () => void;
  onEditRowClick?: (rowId: string, row: any) => void;
  addableColumns?: EnhancedTableColumn[];
  addRowAtBottom?: boolean;
  showEditOnTable?: boolean;
  wmsMode?: boolean;
  densityLevel?: 'comfortable' | 'standard' | 'compact';
}

const EnhancedTableBody: React.FC<EnhancedTableBodyProps> = ({ 
  header, 
  data, 
  selected, 
  handleCheckboxClick, 
  boxedCells = false, 
  boxedCellClass = '', 
  striped = false, 
  stripedColors, 
  actionsColumnId, 
  rowActions, 
  rowActionsOutsideMenu = false, 
  addingRow = false, 
  editingRowId = null, 
  editRowValues = {}, 
  editRowErrors = {}, 
  onEditInputChange, 
  onEditCancel, 
  onEditSave, 
  onEditRowClick, 
  addableColumns, 
  showEditOnTable = false,
  wmsMode = false,
  densityLevel = 'standard'
}) => {
  console.log('EnhancedTableBody data:', data, 'header:', header);
  const theme = useMuiTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);

const parseColor = (colorValue: string): string => {
  if (!colorValue) return '#000000';

  try {
    return chroma(colorValue).hex();
  } catch (error) {
    console.warn('Invalid color:', colorValue);
    return '#000000';
  }
};


const BarcodeDisplay: React.FC<{ value: string; format?: string; width?: number; height?: number }> = ({
  value,
  format = 'CODE128',
  width = 2,
  height = 50
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (canvasRef.current && value) {
      try {
        JsBarcode(canvasRef.current, value, {
          format: format,
          width: width,
          height: height,
          displayValue: true,
          fontSize: 12,
          textMargin: 2,
          margin: 2
        });
      } catch (error) {
        console.error('Barcode generation error:', error);
      }
    }
  }, [value, format, width, height]);

  if (!value) return <Typography variant="body2">No data</Typography>;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      dispatch(showAlert({ message: 'Barcode copied to clipboard!', type: 'success' }));
    } catch (error) {
      dispatch(showAlert({ message: 'Failed to copy barcode.', type: 'error' }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '200px',
        cursor: 'pointer',
        '&:hover': {
          opacity: 0.8,
        },
      }}
      onClick={handleCopy}
      title="Click to copy"
    >
      <canvas ref={canvasRef} style={{ maxWidth: '100%' }} />
    </Box>
  );
};


  const getWMSRowHeight = () => {
    switch (densityLevel) {
      case 'compact': return 32;
      case 'comfortable': return 48;
      default: return 40; // standard
    }
  };

  const getWMSColors = () => {
    if (!wmsMode) return null;
    
    return {
      primary: theme.palette.mode === 'dark' ? '#1976d2' : '#1565c0',
      secondary: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5',
      selected: theme.palette.mode === 'dark' ? '#1976d220' : '#1565c015',
      hover: theme.palette.mode === 'dark' ? '#42424220' : '#f5f5f5',
      border: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0',
      error: '#d32f2f',
      warning: '#ed6c02',
      success: '#2e7d32',
    };
  };

  const wmsColors = getWMSColors();

  const renderCellContent = (value: any, head: EnhancedTableColumn, rowId: string, row: Record<string, any>) => {
    switch (head.type) {
      case EnhancedTableColumnType.TEXT:
        return value;
      case EnhancedTableColumnType.NUMBER:
        return value;
      case EnhancedTableColumnType.DATE:
        return formatDate(value);
      case EnhancedTableColumnType.DATETIME:
        return formatDateTime(value);
      case EnhancedTableColumnType.COLORED_CHIP: {
        const chipStyle = head.chipColors ? head.chipColors[value] : undefined;
        return (
          <Tooltip title={value}>
            <Chip
              label={value}
              style={{
                cursor: "pointer",
                backgroundColor: chipStyle ? chipStyle.backgroundColor : 'white',
                color: chipStyle ? chipStyle.color : 'black',
                minHeight: densityLevel === 'compact' ? '20px' : '24px',
                fontSize: densityLevel === 'compact' ? '0.75rem' : '0.8125rem',
              }}
              sx={{
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'visible',
                  textOverflow: 'unset',
                  lineHeight: '1.2',
                  padding: densityLevel === 'compact' ? '1px 6px' : '2px 8px',
                  maxWidth: '120px',
                },
                display: 'inline-flex',
                maxWidth: '100%',
                height: 'auto',
                minHeight: densityLevel === 'compact' ? '20px' : '24px',
                borderRadius: wmsMode ? '4px' : '16px',
              }}
            />
          </Tooltip>
        );
      }
      case EnhancedTableColumnType.COLOR: {
        const colorValue = parseColor(value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: densityLevel === 'compact' ? 12 : 16,
                height: densityLevel === 'compact' ? 12 : 16,
                backgroundColor: colorValue,
                border: '1px solid',
                borderColor: wmsColors?.border || theme.palette.divider,
                borderRadius: wmsMode ? '2px' : '50%',
                flexShrink: 0,
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                fontSize: densityLevel === 'compact' ? '0.75rem' : '0.875rem'
              }}
            >
              {value}
            </Typography>
          </Box>
        );
      }
      case EnhancedTableColumnType.BARCODE: {
        const barcodeConfig = (head as any).barcodeConfig || {};
        return (
          <BarcodeDisplay 
            value={value} 
            format={barcodeConfig.format || 'CODE128'}
            width={barcodeConfig.width || (densityLevel === 'compact' ? 1 : 2)}
            height={barcodeConfig.height || (densityLevel === 'compact' ? 30 : 40)}
          />
        );
      }
      case EnhancedTableColumnType.LINK:
        return <Link href={typeof (head as any).href === 'function' ? (head as any).href(row) : '#'}>{value}</Link>;
      case EnhancedTableColumnType.CLICKABLE:
        return (
          <Link
            type="button"
            onClick={() => (head as any).onClick(rowId, row)}
            sx={{
              color: theme.palette.text.primary,
              cursor: 'pointer',
              textDecoration: 'underline',
              transition: 'color 0.2s',
              '&:hover': {
                color: wmsColors?.primary || theme.palette.primary.main,
              },
            }}
          >
            {value}
          </Link>
        );
      case EnhancedTableColumnType.CURRENCY:
        return formatCurrencyNumber(value);
      case EnhancedTableColumnType.PHONE_NUMBER:
        return formatIntPhoneNumber(value);
      case EnhancedTableColumnType.ACTION: {
        return null;
      }
      case EnhancedTableColumnType.PROGRESS: {
        const expected = row[(head as any).expectedField];
        const actual = row[(head as any).actualField];
        const dueDate = (head as any).dueDateField ? row[(head as any).dueDateField] : undefined;
        const status = row.status;
        let color: 'success' | 'error' | 'warning' = 'success';
        let icon: React.ReactNode = <CheckCircleIcon color="success" />;
        let helperText = '';
        const isFinished = status === 'Completed' || status === 'cancelled';
        if (!isFinished && dueDate && dayjs().isAfter(dayjs(dueDate))) {
          color = 'warning';
          icon = <WarningIcon color="warning" />;
          helperText = 'Overdue';
        } else if (actual > expected) {
          color = 'error';
          icon = <ErrorIcon color="error" />;
          helperText = 'Over target';
        }
        const percent = expected > 0 ? Math.min((actual / expected) * 100, 100) : 0;
        return (
          <Box sx={{ minWidth: wmsMode ? 120 : 150 }}>
            <Tooltip title={`${actual} / ${expected} hours`} arrow>
              <Stack direction="row" alignItems="center" spacing={1}>
                {icon}
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  color={color}
                  sx={{
                    height: densityLevel === 'compact' ? 6 : 8,
                    borderRadius: wmsMode ? 2 : 6,
                    flex: 1,
                    backgroundColor: wmsColors?.secondary || '#e0e0e0'
                  }}
                />
              </Stack>
            </Tooltip>
            <Box sx={{ pt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography 
                sx={{ 
                  color: theme.palette[color].main,
                  fontSize: densityLevel === 'compact' ? '0.7rem' : '0.75rem'
                }}
              >
                {helperText}
              </Typography>
              <Typography 
                color="text.secondary" 
                fontSize={densityLevel === 'compact' ? 10 : 12}
              >
                {dueDate ? `Due: ${formatDate(dueDate)}` : ''}
              </Typography>
            </Box>
          </Box>
        );
      }
      default:
        return value;
    }
  };

  const renderAddRow = () => (
    <TableRow style={{ backgroundColor: wmsColors?.secondary || theme.palette.action.selected }}>
      <TableCell padding="checkbox" className={boxedCells ? boxedCellClass : undefined} />
      {header.map((column) => {
        // Only render addable columns, skip actions column
        if (!addableColumns?.some(c => c.id === column.id)) {
          return <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined} />;
        }
        return (
          <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined}>
            <TextField
              value={editRowValues[column.id] || ''}
              onChange={e => onEditInputChange && onEditInputChange(column.id, e.target.value)}
              size={densityLevel === 'compact' ? 'small' : 'small'}
              error={!!editRowErrors[column.id]}
              helperText={editRowErrors[column.id]}
              placeholder={column.label}
              autoFocus={header.findIndex(h => h.id === column.id) === 0}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  onEditSave && onEditSave();
                } else if (e.key === 'Escape') {
                  onEditCancel && onEditCancel();
                }
              }}
            />
          </TableCell>
        );
      })}
      <TableCell className={boxedCells ? boxedCellClass : undefined}>
        <Stack direction="row" spacing={1}>
          <Button color="primary" size="small" variant="contained" onClick={onEditSave}>Save</Button>
          <Button color="inherit" size="small" variant="outlined" onClick={onEditCancel}>Cancel</Button>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <TableBody>
      {addingRow && renderAddRow()}
      {Object.entries(data).map(([key, row], idx) => {
        if (editingRowId === key) {
          return (
            <TableRow key={key} style={{ backgroundColor: wmsColors?.secondary || theme.palette.action.selected }}>
              <TableCell padding="checkbox" className={boxedCells ? boxedCellClass : undefined} />
              {header.map((column) => {
                if (column.editable === false || column.type === EnhancedTableColumnType.ACTION) {
                  return <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined} />;
                }
                return (
                  <TableCell key={column.id} className={boxedCells ? boxedCellClass : undefined}>
                    <TextField
                      value={editRowValues[column.id] || ''}
                      onChange={e => onEditInputChange && onEditInputChange(column.id, e.target.value)}
                      size="small"
                      error={!!editRowErrors[column.id]}
                      helperText={editRowErrors[column.id]}
                      placeholder={column.label}
                      autoFocus={header.findIndex(h => h.id === column.id) === 0}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          onEditSave && onEditSave();
                        } else if (e.key === 'Escape') {
                          onEditCancel && onEditCancel();
                        }
                      }}
                    />
                  </TableCell>
                );
              })}
              <TableCell className={boxedCells ? boxedCellClass : undefined}>
                <Stack direction="row" spacing={1}>
                  <Button color="primary" size="small" variant="contained" onClick={onEditSave}>Save</Button>
                  <Button color="inherit" size="small" variant="outlined" onClick={onEditCancel}>Cancel</Button>
                </Stack>
              </TableCell>
            </TableRow>
          );
        }
        
        const isSelected = selected.includes(key);
        let rowBg = theme.palette.background.paper;
        
        if (isSelected) {
          rowBg = wmsColors?.selected || theme.palette.action.selected;
        } else if (striped && stripedColors) {
          let colors = typeof stripedColors === 'function' ? stripedColors(theme) : stripedColors;
          rowBg = colors[idx % 2] || theme.palette.background.paper;
        } else if (wmsMode && striped) {
          rowBg = idx % 2 === 0 
            ? theme.palette.background.paper 
            : (theme.palette.mode === 'dark' ? '#ffffff05' : '#00000005');
        }

        return (
          <TableRow 
            key={key} 
            tabIndex={-1} 
            hover={!isSelected}
            style={{
              backgroundColor: rowBg,
              transition: wmsMode ? 'background-color 0.15s ease' : 'all 0.2s ease',
              height: getWMSRowHeight(),
              border: wmsMode && boxedCells ? `1px solid ${wmsColors?.border}` : 'none',
            }}
            sx={{
              '&:hover': !isSelected ? {
                backgroundColor: wmsColors?.hover || theme.palette.action.hover,
              } : {},
            }}
          >
            <TableCell 
              padding="checkbox" 
              className={boxedCells ? boxedCellClass : undefined} 
              sx={{ 
                padding: theme.spacing(0.5, 1),
                backgroundColor: 'transparent',
              }}
            >
              <Checkbox
                checked={isSelected}
                inputProps={{
                  'aria-labelledby': `enhanced-table-checkbox-${key}`,
                }}
                onClick={() => handleCheckboxClick(key)}
                size={densityLevel === 'compact' ? 'small' : 'small'}
                color="primary"
                sx={{
                  '&.Mui-checked': {
                    color: wmsColors?.primary || theme.palette.primary.main,
                  }
                }}
              />
            </TableCell>
            {header.map((column) => {
              if (actionsColumnId && column.id === actionsColumnId) {
                const allRowActions = (column as any).actions || [];
                const visibleRowActions = allRowActions.filter(action => !action.visible || action.visible(row));
                return (
                  <TableCell
                    key={column.id}
                    align={column.numeric ? 'right' : 'left'}
                    style={{ 
                      padding: theme.spacing(0.5, 1), 
                      color: wmsColors?.primary || theme.palette.primary.main,
                      backgroundColor: 'transparent',
                    }}
                    className={boxedCells ? boxedCellClass : undefined}
                  >
                    {showEditOnTable && onEditRowClick && (
                      <Tooltip title="Edit">
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => onEditRowClick(key, row)} 
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {rowActionsOutsideMenu || visibleRowActions.length === 1 ? (
                      visibleRowActions.map((action, idx) => (
                        <Tooltip title={action.title || action.label || ''} key={action.key || idx}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => action.onClick(key, row)}
                              disabled={action.disabled && action.disabled(row)}
                              color={action.color || 'primary'}
                            >
                              {action.icon}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))
                    ) : (
                      visibleRowActions.length > 1 && (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={e => { setMenuAnchor(e.currentTarget); setMenuRowId(key); }} 
                            color="primary"
                          >
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={menuAnchor}
                            open={menuRowId === key && Boolean(menuAnchor)}
                            onClose={() => { setMenuAnchor(null); setMenuRowId(null); }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                          >
                            {visibleRowActions.map((action, idx) => (
                              <MenuItem key={action.key || idx} onClick={() => { action.onClick(key, row); setMenuAnchor(null); setMenuRowId(null); }}>
                                {action.icon}&nbsp;{action.label}
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )
                    )}
                  </TableCell>
                );
              }
              return (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  style={{ 
                    padding: theme.spacing(0.5, 1), 
                    color: theme.palette.text.primary, 
                    fontSize: densityLevel === 'compact' ? '0.75rem' : theme.typography.body2.fontSize,
                    backgroundColor: 'transparent',
                  }}
                  className={boxedCells ? boxedCellClass : undefined}
                >
                  {column.type === EnhancedTableColumnType.CUSTOM
                    ? column.render?.(row[column.id], row, key)
                    : renderCellContent(row[column.id], column, key, row)}
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
};

export default EnhancedTableBody;